import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
// PickingWindow / PackingWindow / PackingSession are reached through the flow
// registry (pp/flows.ts), not imported here — that is the seam for a 2nd flow.
import SettingsPage from './SettingsPage';
import StatisticsPage from './StatisticsPage';
import WorkTimePage from './WorkTimePage';
import LabelDoctorStats from './statistics/LabelDoctorStats';
import OrderDetailPanel from './OrderDetailPanel';
import ManualLabelPage from './ManualLabelPage';
import WHLabelsPage from './WHLabelsPage';
import ParcelsSection from './statistics/ParcelsSection';
import CustomOrderLabelPage from './CustomOrderLabelPage';
import ProductsPage from './ProductsPage';
import CalibrateWalk, { CalibrateAisles, LayoutFromSections } from './warehouse/CalibrateWalk';
import UnitsWalk from './warehouse/UnitsWalk';
import UnitsPage from './warehouse/UnitsPage';
import MarkStacks from './warehouse/MarkStacks';
import LocationPage from './warehouse/LocationPage';
import PiecesPage from './warehouse/PiecesPage';
import TasksPage from './tasks/TasksPage';
import TaskScreen from './tasks/TaskScreen';
import { useIsPhone, isPhoneNow } from './useIsPhone';
import { WgrDashboard, WgrWorkArea, WgrSection, WgrStatistics, WgrManager } from './WgrInventory';
import { WgrShippingInfo } from './InformationPage';
import InstructionsPage from './InstructionsPage';
import { applyAppearance, cachedPrefs } from './appearance';
import { applyFavicon, cachedFavicon, loadPublicBranding, storeBranding, useBrandingLogo } from './branding';
import { ReturnsPage, ReturnOrderDetail, ReturnReplenish } from './ReturnsPage';
import { ReturnsDesk } from './returns/Desk';
import { OrderNo } from './OrderNo';
import AdminTasksPage from './AdminTasksPage';
import ReviewPage from './ReviewPage';
import { authFetch, API } from './api';
import { startBuildCheck, useBuildStale, reloadFresh } from './buildCheck';
import { detectZebraIfEnabled, presentPdf } from './labelPrint';
import LabelRunPanel from './LabelRunPanel';
import { confirmAndProcess } from './processOrders';
import RoundRecap from './pp/RoundRecap';
import PpApp from './pp2/PpApp';
import FixDialog from './pp2/FixDialog';
import AnnouncementBar from './pp2/AnnouncementBar';
import BringHrxPage, { useBringGo } from './pp2/BringHrxPage';
import PacketaPage, { usePacketaClock } from './pp2/PacketaPage';
import InboundsPage from './inbound/InboundsPage';
import InboundDetail from './inbound/InboundDetail';
import InboundReplenish from './inbound/InboundReplenish';
import InboundMove from './inbound/InboundMove';
import ProductPage from './products/ProductPage';
import ProductMove from './products/ProductMove';
import CantFindReport from './CantFindReport';
import { flowComponents, loadFlows, selectedFlowKey, setSelectedFlowKey, PpFlow } from './pp/flows';
import WhoIsPicking from './pp/WhoIsPicking';
import WhichCart, { Cart } from './pp/WhichCart';
import { setTrackContext } from './pp/track';
import { fmtWh, loadWarehouseTz } from './time';
import { useReprintApproval } from './pp2/ReprintApproval';

// Apply the cached theme immediately so there is no dark-flash before login
applyAppearance(cachedPrefs());
// Same idea for branding: paint the cached favicon now, then refresh from the server.
// This runs before login because the sign-in page shows the logo too.
applyFavicon(cachedFavicon());
loadPublicBranding(API);

interface Tab {
  key: string;
  label: string;
  count: number;
  color?: string | null;
  is_system?: boolean;
  position?: number;
  // "Handle first" (Settings → Tabs; Product exchange today): red count, a notice
  // above the table, and a prompt before a new P&P round starts while any wait.
  urgent?: boolean;
  /** label_issue only: orders whose label is now bought but never printed */
  ready?: number;
}

interface Order {
  id: string;
  /** Customer-facing number: WGR motleyId ('NO28091'), Shopify '#1355', MCF
   *  external_id. Shown in the grid; `id` stays the key we act on. */
  order_number?: string;
  customer: string;
  country_code: string;
  flag: string;
  date: string;
  carrier: string;          // the shop's shipping-method name
  carrier_code?: string;    // the label-buying carrier account behind it
  carrier_name?: string;    // what the warehouse calls it (the filter pill)
  items: number;
  weight: number;
  amount: number;
  currency: string;
  tab: string;
  dispatch_note_url?: string;
  webshop_id?: string;
  /** Set while the order is back in New to send an exchange replacement. */
  exchange_id?: number | null;
  /** 'cod' | 'prepaid' | 'unknown' by the store's own payment-method lists */
  payment_kind?: string;
  /** 'order' | 'resend' — a resend is a WMS-created linked order for a lost parcel. */
  kind?: string;
  parent_order_id?: string | null;
  parent_order_number?: string | null;
  /** an original's resends (number, tab, status) */
  resends?: { id: string; order_number: string; tab: string; status: string }[];
  /** split shipment: "1/2" = parcels dispatched / total (cancelled ones not counted);
   *  open = parcels still to send (open + active); waiting = not yet released;
   *  between = one has left and the next is not released (nothing can be packed or processed) */
  split?: { badge: string; active_parcel_no: number | null; open: number; waiting: number; between: boolean; total: number } | null;
  /** WGR: article numbers on this order waiting for a NOS (never-out-of-stock) supplier delivery */
  nos_waiting?: string[];
  /** Label issue: the label is now bought — print it (if not done) and Process. Stays until the order leaves
   *  Label issue (Fernando 2026-10-09); `printed` = it has been sent to a printer at least once. */
  label_ready?: { carrier: string; tracking: string; label_id: number; printed?: boolean; printed_at?: string | null } | null;
  /** customer mails (docs/CUSTOMER-MAILS.md): the live case — the customer or the store manager is being asked */
  customer_case?: { status: string; step: number; audience: string; kind: string; test?: boolean } | null;
  /** why order_flags pulled the order out of picking (docs/FLAGGED-ORDERS.md) */
  flag_reason?: { rules: { rule: string; text: string; evidence?: string[] }[]; notes?: string[]; at?: string; released_by?: string | null;
                  auto_deleted?: { at?: string; by?: string; shop?: string } | null } | null;
}

interface Webshop {
  id: string;
  name: string;
  platform: string;
}

// Tab key uses underscores; URL slug uses hyphens
function tabToSlug(key: string): string { return key.replace(/_/g, '-'); }
function slugToTab(slug: string): string { return slug.replace(/-/g, '_'); }

const BADGE_COLORS = [
  'bg-blue-900/60 text-blue-300',
  'bg-purple-900/60 text-purple-300',
  'bg-green-900/60 text-green-300',
  'bg-orange-900/60 text-orange-300',
  'bg-pink-900/60 text-pink-300',
  'bg-teal-900/60 text-teal-300',
];


const TAB_ACTIVE: Record<string, string> = {
  blue: 'bg-blue-600', green: 'bg-green-600', yellow: 'bg-yellow-500',
  orange: 'bg-orange-600', red: 'bg-red-600', purple: 'bg-purple-600',
  teal: 'bg-teal-600', pink: 'bg-pink-600',
};
function tabActiveCls(color?: string | null) {
  return TAB_ACTIVE[color || ''] ?? 'bg-blue-600';
}

function storeBadgeClass(id: string, webshops: Webshop[]): string {
  const idx = webshops.findIndex(w => w.id === id);
  return BADGE_COLORS[Math.max(idx, 0) % BADGE_COLORS.length];
}

function storeLabel(id: string, webshops: Webshop[]): string {
  const ws = webshops.find(w => w.id === id);
  if (ws) return ws.name;
  return (id || 'unknown').split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// ── Login ─────────────────────────────────────────────────────────────────────

function LoginPage({ onLogin }: { onLogin: (token: string, username: string) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const logo = useBrandingLogo();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) { setError('Wrong username or password'); setLoading(false); return; }
      const data = await res.json();
      localStorage.setItem('wms_token', data.token);
      localStorage.setItem('wms_username', data.username);
      localStorage.setItem('wms_role', data.role || 'staff');
      localStorage.setItem('wms_first_name', data.first_name || data.username);
      localStorage.setItem('wms_session_id', String(data.session_id || ''));
      // A shared tablet login (Tablet-1/2): picking and packing ask who is working.
      localStorage.setItem('wms_is_device', data.is_device ? '1' : '');
      // a registered cart tablet opens its PIN pad; a P&P station opens the Orders page, whose one button starts a
      // round (Fernando 19.10: "the WMS shouldn't open in a PP screen. It should open at the Orders page")
      localStorage.setItem('wms_pp_device', data.pp_device || '');
      if (data.pp_device === 'tablet') { window.location.href = '/pp'; return; }
      if (data.pp_device === 'station') { window.location.href = '/orders/new'; return; }
      if (data.pp_device === 'moving') { window.location.href = '/tasks'; return; }   // a moving station: Tasks (2.11)
      onLogin(data.token, data.username);
    } catch {
      setError('Could not connect to server');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0f1117] flex items-center justify-center">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          {logo ? (
            <img src={logo} alt="" className="h-16 max-w-[240px] object-contain mx-auto mb-4" />
          ) : (
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center font-bold text-xl text-white mx-auto mb-4">W</div>
          )}
          <h1 className="text-2xl font-bold text-white">Motley Denim WMS</h1>
          <p className="text-gray-500 text-sm mt-1">Sign in to your account</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-[#1a1d27] border border-gray-800 rounded-2xl p-8 space-y-4">
          <div>
            <label className="block text-gray-400 text-sm mb-1.5">Username</label>
            <input value={username} onChange={e => setUsername(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 focus:border-blue-500 text-white rounded-xl px-4 py-3 focus:outline-none"
              autoFocus autoComplete="username" />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-1.5">Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 focus:border-blue-500 text-white rounded-xl px-4 py-3 focus:outline-none"
              autoComplete="current-password" />
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <button type="submit" disabled={loading || !username || !password}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors">
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Orders page ───────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

/** A row of additive filter pills: click to add, click again to remove.
 *
 *  An empty selection means All — the same thing the previous single-select
 *  version meant by '', so "nothing selected" can never mean "show nothing".
 *
 *  Multi-select lives on the pills rather than behind a checkbox dropdown
 *  because the whole option set is small and fixed (3 stores, ~7 carriers): it
 *  fits on one line, picking two is two clicks with no menu to open, and which
 *  ones are lit stays readable instead of collapsing into "2 selected".
 *                                                        (Claude, 2026-08-24) */
function FilterPills({ label, options, selected, onChange }: {
  label: string;
  /** `count` — shown inside the pill: how many orders of the open tab it would
   *  leave on screen (carrier pills, Fernando 2026-09-15). Store pills pass none. */
  options: { id: string; name: string; count?: number }[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const cls = (on: boolean) =>
    `px-3 py-1 text-xs rounded-full font-medium transition-colors ${
      on ? 'bg-blue-600 text-white'
         : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'}`;
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  return (
    <div className="flex items-center gap-2 mb-3 flex-wrap">
      <span className="text-xs text-gray-500 uppercase tracking-wider">{label}:</span>
      <button onClick={() => onChange([])} className={cls(selected.length === 0)}>All</button>
      {options.map(o => (
        <button key={o.id} onClick={() => toggle(o.id)} className={cls(selected.includes(o.id))}
          title={selected.includes(o.id) ? `Remove ${o.name} from the filter`
                                         : `Add ${o.name} — several can be selected`}>
          {o.name}
          {o.count !== undefined && (
            <span className={`ml-1.5 px-1.5 py-px rounded-full text-[10px] font-semibold tabular-nums ${
              selected.includes(o.id) ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-300'}`}>
              {o.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

function OrdersPage({ tabs, webshops, onRefreshTabs, station = false, locked = false }: {
  tabs: Tab[];
  webshops: Webshop[];
  onRefreshTabs: () => void;
  /** a P&P station login: the list to look at and ONE button, Start picking & packing round (Fernando 19.10) */
  station?: boolean;
  /** a Tier 1 person's own login: the list to look at, no actions (Fernando 20.10) */
  locked?: boolean;
}) {
  const isPhone = useIsPhone();                      // phone: fewer columns, a tap opens the order
  // printing notes / labels that were printed before = a reprint: Tier 1 needs a manager's PIN or badge (19.10)
  const reprintApproval = useReprintApproval();
  const { tab: tabSlug = 'new' } = useParams<{ tab: string }>();
  const activeTab = slugToTab(tabSlug);
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = localStorage.getItem('wms_role') || 'staff';

  const [orders, setOrders] = useState<Order[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [fixing, setFixing] = useState(false);       // a station: the Fix dialog for the ticked orders (2.11)
  // a finished fix comes back here with its result (StationApp → navigate state)
  const [fixDone, setFixDone] = useState<string>(() => String((location.state as any)?.fixDone || ''));
  const [carrierFilter, setCarrierFilter] = useState<string[]>([]);
  const [carriers,  setCarriers]  = useState<{ code: string; name: string; count?: number }[]>([]);
  const [storeFilter, setStoreFilter] = useState<string[]>([]);
  // Tab badge counts for the CURRENT filter. Null = unfiltered, so the counts
  // handed down from the app shell are already right and no extra request is
  // made. Without this the strip says New 97 while the grid below it lists 61.
  const [filteredCounts, setFilteredCounts] = useState<Record<string, number> | null>(null);
  const [loading, setLoading] = useState(false);
  const [moveTo, setMoveTo] = useState('');
  const [printingNotes, setPrintingNotes] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [printingLabels, setPrintingLabels] = useState(false);
  const [labelRun, setLabelRun] = useState<{ jobId: string; zebra: any } | null>(null);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  // A link INTO one order opens its panel (Claude, 2026-10-02): `?open=<order id>` (Statistics → Parcels),
  // or `/orders/<order id>` — the Label doctor and Review links have always navigated that way and used to
  // land on an empty "tab". A segment that is no tab is taken as an order id once the tabs are known.
  useEffect(() => {
    const open = new URLSearchParams(location.search).get('open');
    if (open) setDetailOrderId(open);
  }, [location.search]);
  useEffect(() => {
    if (tabs.length && !tabs.some(t => t.key === activeTab) && tabSlug) {
      navigate(`/orders/new?open=${encodeURIComponent(tabSlug)}`, { replace: true });
    }
  }, [tabs, activeTab, tabSlug]);   // eslint-disable-line react-hooks/exhaustive-deps
  const closeDetail = () => {
    setDetailOrderId(null);
    if (new URLSearchParams(location.search).get('open')) navigate(location.pathname, { replace: true });
  };
  const [cantFindReportIds, setCantFindReportIds] = useState<string[] | null>(null);
  const [recapRound, setRecapRound] = useState<number | null>(null);   // "Your last round" popup
  // P&P flow selector (pp/flows.ts): approved flows for everyone, testing flows
  // for admins. The chosen key rides with the Printed move (starts the round
  // under that flow) and with every tracked event.
  const [flows, setFlows] = useState<PpFlow[]>([]);
  const [flowKey, setFlowKey] = useState<string>(() => selectedFlowKey());
  useEffect(() => {
    loadFlows().then(d => {
      setFlows(d.flows || []);
      if (!(d.flows || []).some(f => f.key === selectedFlowKey())) {
        setSelectedFlowKey(d.default || 'default'); setFlowKey(d.default || 'default');
      }
    }).catch(() => {});
  }, []);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  // Wording for handle-first tabs, editable under Settings → Packing.
  const [packingTexts, setPackingTexts] = useState<{ urgent_notice: string; urgent_prompt: string }>({
    urgent_notice: '{tab} orders are handled first. Pack them as soon as possible, between pick-and-pack sessions — before a new round is started.',
    urgent_prompt: '{count} {tab} order(s) are waiting.\n\nPack those first — they go out between pick-and-pack sessions.\n\nStart this round anyway?',
  });
  useEffect(() => {
    authFetch(`${API}/packing-texts`).then(async r => {
      if (r.ok) { const d = await r.json(); setPackingTexts({ urgent_notice: d.urgent_notice, urgent_prompt: d.urgent_prompt }); }
    }).catch(() => {});
  }, []);
  const fillText = (t: string, vars: Record<string, string | number>) =>
    t.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = parseInt(localStorage.getItem('wms_page_size') || '50');
    return PAGE_SIZE_OPTIONS.includes(saved) ? saved : 50;
  });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // the row a search landed on, ringed for a moment so it is "shown for opening"
  const [highlightId, setHighlightId] = useState<string | null>(null);

  // Arrays get a new identity every render, so the joined form is what the hook
  // dependency lists compare on — otherwise every render refetches.
  const labelKey = carrierFilter.join(',');
  const storeKey = storeFilter.join(',');
  const filterQ = (labelKey ? `&carrier=${encodeURIComponent(labelKey)}` : '')
                + (storeKey ? `&webshop_id=${encodeURIComponent(storeKey)}` : '');

  // The carrier pills and their counts: orders of THIS tab in the selected
  // stores, per carrier account — the same join as /api/orders, so the number on
  // the pill is the number the grid shows once it is clicked. Re-fetched with the
  // grid, so a move updates both. The SELECTION is deliberately kept: building a
  // two-carrier filter and losing it just by looking at another tab is the
  // annoying part of a filter like this.
  const fetchCarriers = useCallback(() => {
    const q = `?tab=${encodeURIComponent(activeTab)}` + (storeKey ? `&webshop_id=${encodeURIComponent(storeKey)}` : '');
    authFetch(`${API}/shipping/filter-carriers${q}`).then(r => r.ok ? r.json() : []).then(setCarriers).catch(() => {});
  }, [activeTab, storeKey]);

  const fetchOrders = useCallback(async (pg = page) => {
    setLoading(true);
    setSelected(new Set());
    const _searchQ = search ? `&search=${encodeURIComponent(search)}` : '';
    const res = await authFetch(`${API}/orders?tab=${activeTab}&page=${pg}&page_size=${pageSize}${_searchQ}${filterQ}`);
    if (res.status === 401) { window.location.href = '/'; return; }
    const data = await res.json();
    setOrders(data.orders ?? []);
    setTotal(data.total ?? 0);
    setLoading(false);
    fetchCarriers();
  }, [activeTab, page, pageSize, search, filterQ, fetchCarriers]);

  // debounce the search box, reset to page 1 when the term changes
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 when tab changes
  useEffect(() => { setPage(1); }, [activeTab]);

  // ...and when the filter changes, because page 7 of an unfiltered list is
  // usually past the end of a filtered one, which shows up as an empty grid.
  useEffect(() => { setPage(1); }, [filterQ]);


  useEffect(() => { fetchOrders(page); }, [activeTab, page, pageSize, search, filterQ]);  // eslint-disable-line

  // A search jumps to the tab the order is in (Fernando, 2026-09-09): the pill of
  // that tab lights up, the URL follows, the search text stays, the first match
  // is ringed. Results can span tabs — the newest match decides, and every result
  // row shows its own tab pill. Navigating here must NOT clear the search: only a
  // pill CLICK does that (see the tab bar), which is why the clearing lives in
  // the click handler and not in an effect on activeTab.
  useEffect(() => {
    if (!search || loading || orders.length === 0) return;
    const first = orders[0];
    if (first.tab && first.tab !== activeTab) {
      navigate(`/orders/${tabToSlug(first.tab)}`, { replace: true });
    }
    setHighlightId(first.id);
    const t = setTimeout(() => setHighlightId(null), 2500);
    return () => clearTimeout(t);
  }, [search, loading, orders]);  // eslint-disable-line

  // Clicking a tab while a search is active leaves the search: the box is
  // cleared, the tab's own list loads, the badge counts refresh (the effect on
  // activeTab below).
  const clickTab = (key: string) => {
    if (searchInput || search) { setSearchInput(''); setSearch(''); setPage(1); }
    navigate(`/orders/${tabToSlug(key)}`);
  };

  // Switching a tab re-reads the badge counts too, not just the rows. They used
  // to be fetched once when the app loaded, so anything a COLLEAGUE did stayed
  // invisible until you reloaded the page — which is why the tabs could not be
  // trusted and Fernando had got into the habit of hitting refresh. The rows
  // were always re-fetched; only the numbers above them were stale.
  useEffect(() => { onRefreshTabs(); }, [activeTab]);  // eslint-disable-line

  // Tab badges follow the filter, so the strip and the grid never disagree.
  useEffect(() => {
    if (!filterQ) { setFilteredCounts(null); return; }
    let live = true;
    authFetch(`${API}/tabs?${filterQ.slice(1)}`)
      .then(r => r.ok ? r.json() : null)
      .then((ts: Tab[] | null) => {
        if (live && ts) setFilteredCounts(Object.fromEntries([
          ...ts.map(t => [t.key, t.count] as [string, number]),
          ...ts.filter(t => t.ready != null).map(t => [`${t.key}:ready`, t.ready!] as [string, number])]));
      })
      .catch(() => { /* leave the unfiltered counts rather than blanking them */ });
    return () => { live = false; };
  }, [filterQ]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const goToPage = (p: number) => {
    const clamped = Math.min(Math.max(1, p), totalPages);
    setPage(clamped);
  };

  const changePageSize = (size: number) => {
    localStorage.setItem('wms_page_size', String(size));
    setPageSize(size);
    setPage(1);
  };

  const refresh = useCallback(() => { fetchOrders(page); onRefreshTabs(); }, [fetchOrders, onRefreshTabs, page]);

  const toggleAll = () => setSelected(selected.size === orders.length ? new Set() : new Set(orders.map(o => o.id)));
  const toggleOne = (id: string) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const moveOrders = async () => {
    if (!moveTo || selected.size === 0) return;
    // Moving to Printed STARTS a pick-and-pack round. If handle-first orders
    // (Product exchange) are waiting, ask to pack those first — unless the orders
    // being moved ARE those orders, which is the point. Continuing is allowed and
    // is recorded on the round's start event. (Fernando, 2026-09-14)
    let urgentWaiting: Record<string, number> | undefined;
    if (moveTo === 'printed') {
      const waiting: Record<string, number> = {};
      for (const t of tabs) {
        if (!t.urgent || !t.count) continue;
        const movingFromHere = activeTab === t.key ? selected.size : 0;
        const left = t.count - movingFromHere;
        if (left > 0) waiting[t.key] = left;
      }
      const entries = Object.entries(waiting);
      if (entries.length) {
        // one message per waiting tab, wording from Settings → Packing
        const msg = entries.map(([k, n]) =>
          fillText(packingTexts.urgent_prompt, { tab: tabs.find(t => t.key === k)?.label || k, count: n })
        ).join('\n\n');
        if (!window.confirm(msg)) return;
        urgentWaiting = waiting;
      }
    }
    await authFetch(`${API}/orders/move`, {
      method: 'POST',
      body: JSON.stringify({ ids: Array.from(selected), tab: moveTo, flow: flowKey,
                             ...(urgentWaiting ? { urgent_waiting: urgentWaiting } : {}) }),
    });
    refresh();
    setMoveTo('');
  };

  const printDispatchNotes = async () => {
    if (selected.size === 0 || printingNotes) return;
    // Open window synchronously before async fetch so popup blocker doesn't fire
    const notesWin = window.open('', '_blank');
    if (notesWin) notesWin.document.write('<p style="font-family:sans-serif;padding:2rem">Generating delivery notes...</p>');
    setPrintingNotes(true);
    try {
      const res = await reprintApproval.run(approver => authFetch(`${API}/orders/dispatch-notes`, {
        method: 'POST', body: JSON.stringify({ ids: Array.from(selected), ...approver }),
      }));
      if (!res) { if (notesWin) notesWin.close(); return; }   // the reprint approval was cancelled
      if (!res.ok) {
        if (notesWin) notesWin.close();
        const errData = await res.json().catch(() => ({}));
        alert(errData.detail || 'No dispatch notes found for the selected orders.');
        return;
      }
      const blob = await res.blob();
      // Toolbar + server-rendered page images, no download-mode detection
      // (see labelPrint.ts). The token lets the popup fetch pages by reference
      // instead of posting the whole PDF back — a 50-order run is ~100 pages.
      presentPdf(notesWin, blob,
                 { title: 'Delivery notes', filename: 'delivery-notes.pdf', autoPrint: true },
                 res.headers.get('X-Doc-Token'));
      refresh();
    } finally { setPrintingNotes(false); }
  };

  const processOrders = async () => {
    if (selected.size === 0 || processing) return;
    const items = orders.filter(o => selected.has(o.id)).map(o => ({
      id: o.id, order_number: o.order_number, split: o.split, labelUnprinted: !!o.label_ready && !o.label_ready.printed }));
    // selected ids not on this page (kept across pages) still go, as plain items
    selected.forEach(id => { if (!items.some(i => i.id === id)) items.push({ id, order_number: id, split: null, labelUnprinted: false }); });
    setProcessing(true);
    try {
      const result = await confirmAndProcess(items);
      refresh();
      // the round's last order went: the P&Per's recap (Fernando, 2026-09-28)
      const closed = result?.rounds_closed || [];
      if (closed.length) setRecapRound(closed[closed.length - 1]);
    } finally { setProcessing(false); }
  };

  const deleteOrders = async () => {
    if (selected.size === 0 || deleting) return;
    const count = selected.size;
    if (!window.confirm(
      `Delete ${count} order${count !== 1 ? 's' : ''}?\n\n`
      + `This will attempt to cancel them in the platform and move them to the Deleted tab.`
    )) return;
    setDeleting(true);
    try {
      const res = await authFetch(`${API}/orders/delete`, {
        method: 'POST', body: JSON.stringify({ ids: Array.from(selected) }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Delete failed'); return; }
      const warnings = (data.results || []).filter((r: any) => r.warning);
      if (warnings.length > 0) {
        alert(`${data.deleted} order${data.deleted !== 1 ? 's' : ''} moved to Deleted.\n\n`
          + `Platform notes:\n` + warnings.map((r: any) => `  Order ${r.order_number || r.order_id}: ${r.warning}`).join('\n'));
      }
      refresh();
    } finally { setDeleting(false); }
  };

  // The batch runs on the server and reports back as it goes; LabelRunPanel shows
  // it and prints whatever is ready. No window is opened here any more — it is
  // opened inside the panel's Print click, which is the gesture the browser
  // trusts, and it means a slow carrier no longer leaves a blank tab sitting
  // open saying "Generating labels..." with nothing behind it.
  const printLabels = async () => {
    if (selected.size === 0 || printingLabels) return;
    // Pack-time label print (2026-09-06, docs/PACK-TIME-LABEL.md): the stored
    // label now comes out when the packer scans the delivery note. Bulk print
    // stays available — labels printed here are recorded in the output ledger,
    // so pack-time will not print them twice — but the person is told first.
    // Fernando's wording. Remove once the new flow is the habit.
    if (!window.confirm(
      'Testing new feature: Shipping labels will be automatically printed one at a time '
      + 'every time you scan a new delivery note. '
      + 'Are you sure you want to print labels in bulk now?')) return;
    setPrintingLabels(true);
    try {
      const zebra = await detectZebraIfEnabled();
      const res = await reprintApproval.run(approver => authFetch(`${API}/orders/print-labels/start`, {
        method: 'POST', body: JSON.stringify({ ids: Array.from(selected), format: zebra ? 'zpl' : 'pdf', ...approver }),
      }));
      if (!res) return;                                  // the reprint approval was cancelled
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Label generation could not be started'); return; }
      setLabelRun({ jobId: data.job_id, zebra });
    } finally { setPrintingLabels(false); }
  };

  const startPicking = () => {
    sessionStorage.setItem('wms_picking_ids', JSON.stringify(Array.from(selected)));
    navigate('/picking');
  };

  const startPacking = () => {
    // Fernando, 2026-10-05: packing is the scan session only — "even if an order is selected, Start packing
    // should only wait for the barcode". The tick-and-pack window recorded no scans, so its orders never
    // counted as packed and held their round (rule 25).
    sessionStorage.removeItem('wms_packing_ids');
    navigate('/packing');
  };


  return (
    <div className="p-3 md:p-6">
      {reprintApproval.box}
      {station && fixing && (
        <FixDialog ids={Array.from(selected)} onClose={() => setFixing(false)}
          onStarted={() => {
            // the fix ends back on this page (Fernando 2.11: "finishes on the same orders page when we fix orders")
            try { sessionStorage.setItem('wms_fix_from', location.pathname + location.search); } catch { /* → Orders New */ }
            setFixing(false); setSelected(new Set()); navigate('/pp');
          }} />
      )}
      {fixDone && (
        <div className="mb-4 flex items-center gap-3 rounded-xl border-2 border-emerald-600 bg-emerald-900/20 px-4 py-3 text-emerald-100 text-lg font-semibold">
          <span>{fixDone}</span>
          <button onClick={() => setFixDone('')} className="ml-auto text-emerald-300 hover:text-white text-sm">✕</button>
        </div>
      )}
      {labelRun && (
        <LabelRunPanel
          jobId={labelRun.jobId}
          zebra={labelRun.zebra}
          onClose={() => { setLabelRun(null); refresh(); }}
          onPrinted={() => refresh()}
        />
      )}

      {/* stations and Tier 1 people open orders to look at them (Fernando 21.10); the server refuses their changes */}
      {detailOrderId && (
        <OrderDetailPanel
          orderId={detailOrderId}
          lookOnly={station || locked}
          tabs={tabs}
          onClose={closeDetail}
          onMoved={() => { refresh(); closeDetail(); }}
          onChanged={() => refresh()}
          onOpenOrder={id => { refresh(); setDetailOrderId(id); }}
          onProcessed={r => { const c = r.rounds_closed || []; if (c.length) setRecapRound(c[c.length - 1]); }}
        />
      )}

      {recapRound != null && <RoundRecap roundId={recapRound} onClose={() => setRecapRound(null)} />}

      {cantFindReportIds && (
        <CantFindReport
          ids={cantFindReportIds}
          storeNames={Object.fromEntries(webshops.map(ws => [ws.id, ws.name]))}
          onClose={() => setCantFindReportIds(null)}
        />
      )}

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Orders</h1>
          <p className="text-gray-500 text-sm mt-0.5 hidden sm:block">Motley Denim warehouse</p>
        </div>
        <div className="relative w-full md:w-80">
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search order #, email, or name…"
            className="w-full px-3 py-2 pr-8 rounded-lg bg-[#1a1d27] border border-gray-700 text-sm text-white focus:outline-none focus:border-blue-500"
          />
          {searchInput && (
            <button onClick={() => setSearchInput('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-sm">✕</button>
          )}
        </div>
      </div>
      {search && (
        <div className="mb-3 text-sm text-gray-400">
          Search results for “{search}” — {total} order{total !== 1 ? 's' : ''} across all tabs; the tab of the
          newest match is selected. Click a number to open it, or click a tab to leave the search.
          <button onClick={() => setSearchInput('')} className="text-blue-400 hover:underline ml-1">clear</button>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex flex-wrap gap-1 mb-4 border-b border-gray-800 pb-3">
        {tabs.map(tab => {
          const count = filteredCounts ? (filteredCounts[tab.key] ?? 0) : tab.count;
          const ready = filteredCounts ? (filteredCounts[`${tab.key}:ready`] ?? 0) : (tab.ready ?? 0);
          // Flagged orders (Fernando, 2026-09-29): for the people who decide — managers and
          // admins — the tab lights up like an urgent tab while something is in it. Not the
          // `urgent` flag itself: that would also make the packing round ask to pack them.
          const flaggedForMe = tab.key === 'flagged' && count > 0 && (userRole === 'admin' || userRole === 'manager');
          return (
          <button key={tab.key} onClick={() => clickTab(tab.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? `${tabActiveCls(tab.color)} text-white`
                : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
            }${flaggedForMe ? ' ring-2 ring-red-500/70' : ''}`}>
            {tab.label}
            {count > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                // the NUMBER goes red on a handle-first tab; the tab itself keeps its
                // colour (Fernando: a calm screen for eight hours a day)
                tab.urgent || flaggedForMe ? 'bg-red-600 text-white font-semibold'
                : activeTab === tab.key ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-300'
              }`} title={flaggedForMe ? 'Flagged orders waiting for a decision — CS releases, a manager can trust the customer'
                         : tab.urgent ? 'Handle first — between pick-and-pack sessions' : undefined}>
                {count}
              </span>
            )}
            {/* Label issue: labels now bought but not printed — a packed parcel can go */}
            {ready > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-600 text-white font-semibold whitespace-nowrap"
                title={`${ready} order${ready > 1 ? 's have' : ' has'} a label ready — open ${ready > 1 ? 'them' : 'it'}, print the label if it is not on the parcel yet, and Process`}>
                🏷 {ready} ready
              </span>
            )}
          </button>
          );
        })}
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {station ? (<>
          <button onClick={() => navigate('/pp')}
            className="px-8 py-4 bg-emerald-600 hover:bg-emerald-500 text-white text-xl font-black rounded-xl transition-colors">
            Start picking & packing round
          </button>
          {/* Fernando 2.11: tick orders that left Printed (Can't find, Pending, Product exchange, Label issue …) → Fix →
              a manager's badge or PIN → their notes print, and they are packed and processed here */}
          {selected.size > 0 && (
            <button onClick={() => setFixing(true)}
              className="px-6 py-4 bg-amber-600 hover:bg-amber-500 text-white text-xl font-black rounded-xl transition-colors">
              Fix {selected.size} selected
            </button>
          )}
        </>) : locked ? (
          <div className="px-4 py-3 rounded-lg bg-[#1a1d27] border border-gray-700 text-gray-200 text-base">
            Picking &amp; packing is done at the <b>P&amp;P station</b> with your PIN — this page is for looking only.
          </div>
        ) : (<>
        <button onClick={startPacking}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
          Start packing
        </button>
        <button disabled={selected.size === 0} onClick={startPicking}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors">
          View picking list
        </button>
        {flows.length > 1 && (
          <select value={flowKey} title="P&P flow — how picking and packing are done"
            onChange={e => { setFlowKey(e.target.value); setSelectedFlowKey(e.target.value); }}
            className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500">
            {flows.map(f => (
              <option key={f.key} value={f.key}>Flow: {f.label}{f.status === 'testing' ? ' (testing)' : ''}</option>
            ))}
          </select>
        )}
        {(activeTab === 'cant_find' || activeTab === 'hard_to_find') && (
          <button disabled={selected.size === 0}
            onClick={() => setCantFindReportIds(Array.from(selected).map(String))}
            title="Per not-found item: article, picking list, shelf, remaining stock, and each store's sales history with a computed stock level"
            className="px-4 py-2 bg-amber-700 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors">
            Can't find report
          </button>
        )}
        <button disabled={selected.size === 0 || printingNotes} onClick={printDispatchNotes}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors">
          {printingNotes ? 'Generating...' : 'Print delivery notes'}
        </button>
        <button disabled={selected.size === 0 || printingLabels} onClick={printLabels}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors">
          {printingLabels ? 'Generating...' : 'Print shipping labels'}
        </button>
        {userRole !== 'viewer' && (<>
          <button disabled={selected.size === 0 || processing} onClick={processOrders}
            className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors">
            {processing ? 'Processing...' : 'Process orders'}
          </button>
          <div className="flex items-center gap-2">
            <select value={moveTo} onChange={e => setMoveTo(e.target.value)}
              className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500">
              <option value="">Move to...</option>
              {tabs.filter(t => t.key !== activeTab).map(t => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
            <button onClick={moveOrders} disabled={!moveTo || selected.size === 0}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors">
              Move
            </button>
          </div>
        </>)}
        </>)}
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-sm">Show</span>
          <select value={pageSize} onChange={e => changePageSize(Number(e.target.value))}
            className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-2 py-2 focus:outline-none focus:border-blue-500">
            {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <span className="text-gray-500 text-sm">
          {selected.size > 0 ? `${selected.size} selected` : `${total} orders`}
        </span>
      </div>

      {/* Store filter pills */}
      {webshops.length > 1 && (
        <FilterPills label="Store" selected={storeFilter} onChange={setStoreFilter}
          options={webshops.map(ws => ({ id: ws.id, name: ws.name }))} />
      )}

      {/* Carrier filter pills: who the parcel is handed to (Fernando, 2026-09-15) */}
      {carriers.length > 0 && (
        <FilterPills label="Carrier" selected={carrierFilter} onChange={setCarrierFilter}
          options={carriers.map(c => ({ id: c.code, name: c.name, count: c.count }))} />
      )}

      {/* Handle-first tab: say so where the work happens (Fernando, 2026-09-14) */}
      {(() => {
        const t = tabs.find(x => x.key === activeTab);
        return t?.urgent && (t.count > 0 || total > 0) ? (
          <div className="mb-3 px-4 py-2 rounded-lg border-l-4 border-red-600 bg-red-950/30 text-sm text-red-100 whitespace-pre-line">
            {fillText(packingTexts.urgent_notice, { tab: t.label, count: t.count })}
          </div>
        ) : null;
      })()}

      {/* Orders table */}
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
              <th className="w-10 px-4 py-3 text-left">
                <input type="checkbox"
                  checked={selected.size === orders.length && orders.length > 0}
                  onChange={toggleAll}
                  className="rounded border-gray-600 bg-gray-700 accent-blue-500 cursor-pointer" />
              </th>
              <th className="px-4 py-3 text-left">Order #</th>
              <th className="hidden md:table-cell px-4 py-3 text-left">Store</th>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="hidden md:table-cell px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Items</th>
              <th className="hidden md:table-cell px-4 py-3 text-left">Carrier</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="hidden md:table-cell px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="text-center py-12 text-gray-500">Loading...</td></tr>
            )}
            {!loading && orders.length === 0 && (
              /* "No orders in this tab" is a lie when a filter is hiding them,
                 and it is the same blank grid either way — so a tab you had
                 filtered looks broken rather than filtered. Say which it is,
                 and offer the way out. */
              <tr><td colSpan={9} className="text-center py-12 text-gray-500">
                {storeFilter.length || carrierFilter.length ? (
                  <>
                    <div>No orders in this tab match the filter.</div>
                    <button
                      onClick={() => { setStoreFilter([]); setCarrierFilter([]); }}
                      className="mt-2 px-3 py-1 text-xs rounded-full bg-gray-700 hover:bg-gray-600 text-gray-200">
                      Clear {[...storeFilter.map(id => webshops.find(w => w.id === id)?.name || id),
                              ...carrierFilter.map(c => carriers.find(x => x.code === c)?.name || c)].join(', ')}
                    </button>
                  </>
                ) : 'No orders in this tab'}
              </td></tr>
            )}
            {!loading && orders.map((order, i) => (
              <tr key={order.id} onClick={() => (isPhone ? setDetailOrderId(order.id) : toggleOne(order.id))}
                className={`border-b border-gray-800 cursor-pointer transition-colors ${
                  highlightId === order.id ? 'ring-2 ring-inset ring-amber-400 bg-amber-900/20'
                  : selected.has(order.id) ? 'bg-blue-900/30'
                  : i % 2 === 0 ? 'hover:bg-gray-800/50'
                  : 'bg-gray-900/20 hover:bg-gray-800/50'
                }`}>
                <td className="px-2 md:px-4 py-3" onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(order.id)} onChange={() => toggleOne(order.id)}
                    className="rounded border-gray-600 bg-gray-700 accent-blue-500 cursor-pointer" />
                </td>
                <td className="px-2 md:px-4 py-3">
                  <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
                    {/* the number opens the order; the icon beside it copies */}
                    <OrderNo value={order.order_number || order.id} className="text-blue-400 font-medium"
                      onOpen={() => setDetailOrderId(order.id)} />
                    {/* while searching, results come from every tab — say which */}
                    {search && order.tab && order.tab !== activeTab && (
                      <span onClick={e => { e.stopPropagation(); navigate(`/orders/${tabToSlug(order.tab)}`, { replace: true }); }}
                        className="text-xs px-1.5 py-0.5 rounded bg-gray-700 text-gray-300 whitespace-nowrap cursor-pointer hover:bg-gray-600"
                        title="The tab this order is in — click to select it">
                        {tabs.find(t => t.key === order.tab)?.label || order.tab}
                      </span>
                    )}
                    {/* This order has already shipped once and is back to send a
                        replacement. Without the badge a picker just sees an old
                        order inexplicably in New. */}
                    {order.exchange_id && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-purple-600 text-white whitespace-nowrap"
                        title="Exchange — only the replacement item is picked">
                        exchange
                      </span>
                    )}
                    {/* Label issue: the label is bought — it stays until the order is processed (Fernando 9.10) */}
                    {order.label_ready && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-600 text-white font-bold whitespace-nowrap"
                        title={order.label_ready.printed
                          ? `Label ready (${order.label_ready.carrier} ${order.label_ready.tracking}) — sent to the printer ${fmtWh(order.label_ready.printed_at || null, 'short')}. Stick it on the parcel and Process`
                          : `Label ready (${order.label_ready.carrier} ${order.label_ready.tracking}) — open the order, print the label and Process`}>
                        🏷 Label ready
                      </span>
                    )}
                    {order.customer_case && (
                      <span className={`text-xs px-1.5 py-0.5 rounded font-bold whitespace-nowrap ${order.customer_case.status === 'needs_person' ? 'bg-red-700 text-white' : 'bg-sky-800 text-white'}`}
                        title={order.customer_case.audience === 'internal'
                          ? 'The store manager was mailed to fix the ' + (order.customer_case.kind === 'phone_refused' ? 'phone number' : 'name')
                          : 'The customer was asked by e-mail for the ' + (order.customer_case.kind === 'phone_refused' ? 'phone number' : 'name') + ' — open the order for the mails'}>
                        📨 {order.customer_case.test ? 'TEST ' : ''}{order.customer_case.status === 'needs_person' ? 'needs a person'
                          : order.customer_case.audience === 'internal' ? 'manager asked'
                          : order.customer_case.step < 0 ? 'asking soon' : `asked ${order.customer_case.step + 1}/3`}
                      </span>
                    )}
                    {/* Flagged by the rules (known unclaimed-COD customer, 3rd COD order in 72 h, …): the
                        reason rides with the row; the panel has the evidence and the Release / Trust buttons */}
                    {order.flag_reason && (order.flag_reason.rules || []).length > 0 && !order.flag_reason.released_by && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-700 text-white font-bold whitespace-nowrap"
                        title={order.flag_reason.rules.map(r => r.text + (r.evidence?.length ? ` — ${r.evidence.slice(0, 3).join(', ')}` : '')).join('\n')}>
                        ⚑ {order.flag_reason.auto_deleted ? 'Deleted — flagged: ' : ''}{order.flag_reason.rules[0].text}
                      </span>
                    )}
                    {/* WGR: a line waits for a NOS supplier delivery — the same red pill the shop admin shows */}
                    {(order.nos_waiting || []).length > 0 && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-700 text-white font-bold whitespace-nowrap"
                        title={`Waiting for a NOS supplier delivery: ${order.nos_waiting!.join(', ')} — cannot be picked until it arrives; split the shipment to send the rest now`}>
                        NOS{order.nos_waiting!.length > 1 ? ` ×${order.nos_waiting!.length}` : ''}
                      </span>
                    )}
                    {/* A split shipment: this order leaves in several parcels (docs/SPLIT-SHIPMENTS.md) */}
                    {order.split && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-sky-800 text-sky-100 whitespace-nowrap"
                        title={`Split shipment: ${order.split.badge} parcels sent${order.split.active_parcel_no ? ` — parcel ${order.split.active_parcel_no} is being picked/packed` : order.split.between ? ' — the next parcel waits to be released (nothing to pack or process until then)' : order.split.open > 0 ? '' : ' — all parcels sent'}`}>
                        📦 {order.split.badge}
                      </span>
                    )}
                    {/* A resend: the goods of another order sent again after the
                        carrier lost the parcel. Click opens the original. */}
                    {order.kind === 'resend' && (
                      <span onClick={e => { e.stopPropagation(); if (order.parent_order_id) setDetailOrderId(order.parent_order_id); }}
                        className="text-xs px-1.5 py-0.5 rounded bg-amber-600 text-white whitespace-nowrap cursor-pointer hover:bg-amber-500"
                        title={`Resend of ${order.parent_order_number || 'another order'} — click to open the original`}>
                        resend of {order.parent_order_number || '…'}
                      </span>
                    )}
                    {(order.resends || []).map(r => (
                      <span key={r.id} onClick={e => { e.stopPropagation(); setDetailOrderId(r.id); }}
                        className={`text-xs px-1.5 py-0.5 rounded whitespace-nowrap cursor-pointer ${
                          r.status === 'cancelled' ? 'bg-gray-700 text-gray-400 line-through' : 'bg-amber-900/60 text-amber-200 hover:bg-amber-800/60'}`}
                        title={`Re-sent as ${r.order_number} (${r.status}${r.status === 'open' ? ', in ' + r.tab.replace(/_/g, ' ') : ''}) — click to open`}>
                        re-sent as {r.order_number}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="hidden md:table-cell px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${storeBadgeClass(order.webshop_id || '', webshops)}`}>
                    {storeLabel(order.webshop_id || '', webshops)}
                  </span>
                </td>
                <td className="px-2 md:px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span>{order.flag}</span>
                    <span className="text-white">{order.customer}</span>
                  </div>
                  {/* on a phone the hidden columns ride along under the name */}
                  <div className="md:hidden mt-1 flex flex-wrap gap-1.5 text-xs text-gray-400">
                    <span className={`px-1.5 py-0.5 rounded-full ${storeBadgeClass(order.webshop_id || '', webshops)}`}>{storeLabel(order.webshop_id || '', webshops)}</span>
                    <span>{fmtWh(order.date, 'datetime')}</span>
                    <span>{order.carrier_name || order.carrier}</span>
                  </div>
                </td>
                <td className="hidden md:table-cell px-4 py-3 text-gray-400 whitespace-nowrap">{fmtWh(order.date, 'datetime')}</td>
                <td className="px-2 md:px-4 py-3 text-gray-300">{order.items}</td>
                <td className="hidden md:table-cell px-4 py-3 text-gray-400">
                  {order.carrier_name
                    ? <span className="text-xs font-medium bg-gray-700 text-gray-200 px-2 py-0.5 rounded" title={order.carrier}>{order.carrier_name}</span>
                    : <span className="text-gray-500 text-sm">{order.carrier}</span>}
                </td>
                <td className="px-2 md:px-4 py-3 text-right text-white font-medium whitespace-nowrap">
                  {/* cash on delivery: the carrier collects this amount at the door */}
                  {order.payment_kind === 'cod' && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-600 text-white mr-2 align-middle"
                      title="Cash on delivery — the carrier collects the amount">COD</span>
                  )}
                  {order.amount.toFixed(2)} {order.currency}
                </td>
                <td className="hidden md:table-cell px-4 py-3">
                  <button onClick={e => { e.stopPropagation(); setDetailOrderId(order.id); }}
                    className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-md transition-colors">
                    Show
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
          <span className="text-gray-500 text-sm">
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => goToPage(1)} disabled={page === 1}
              className="px-2 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-gray-300 rounded-lg transition-colors">
              «
            </button>
            <button onClick={() => goToPage(page - 1)} disabled={page === 1}
              className="px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-gray-300 rounded-lg transition-colors">
              ‹ Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce<(number | '…')[]>((acc, p, i, arr) => {
                if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('…');
                acc.push(p);
                return acc;
              }, [])
              .filter(p => !isPhone || p === page)                 // phone: just the current page
              .map((p, i) => p === '…'
                ? <span key={`e${i}`} className="px-2 text-gray-600">…</span>
                : <button key={p} onClick={() => goToPage(p as number)}
                    className={`w-9 py-1.5 text-sm rounded-lg transition-colors ${
                      page === p ? 'bg-blue-600 text-white' : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                    }`}>
                    {p}
                  </button>
              )}
            <button onClick={() => goToPage(page + 1)} disabled={page === totalPages}
              className="px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-gray-300 rounded-lg transition-colors">
              Next ›
            </button>
            <button onClick={() => goToPage(totalPages)} disabled={page === totalPages}
              className="px-2 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-gray-300 rounded-lg transition-colors">
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Picking / Packing route wrappers ──────────────────────────────────────────

function PickingRoute() {
  const navigate = useNavigate();
  const orderIds: string[] = (() => {
    try { return JSON.parse(sessionStorage.getItem('wms_picking_ids') || '[]'); }
    catch { return []; }
  })();

  if (!orderIds.length) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-white text-lg">No orders selected for picking</p>
          <button onClick={() => navigate('/orders/new')}
            className="px-5 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors">
            Back to orders
          </button>
        </div>
      </div>
    );
  }

  return <PickingGate orderIds={orderIds} onClose={() => navigate(-1)} />;
}

// Resolve the round and the person BEFORE the picking walk opens: on a shared
// tablet the "Who is picking?" screen; on a person's own login it records them
// and passes straight through. The walk itself comes from the selected flow.
function PickingGate({ orderIds, onClose }: { orderIds: string[]; onClose: () => void }) {
  const [ctx, setCtx] = useState<{ roundId: number | null; actor: string | null; viewOnly?: boolean } | null>(null);
  // Which cart is being taken — asked after "who", skipped when only one cart
  // exists (WhichCart resolves itself). Not per account: tablets swap for charging.
  const [cart, setCart] = useState<Cart | null | undefined>(undefined);
  if (!ctx) return <WhoIsPicking orderIds={orderIds} role="picker" onReady={setCtx} onCancel={onClose} />;
  if (cart === undefined) return <WhichCart orderCount={orderIds.length} onReady={setCart} onCancel={onClose} />;
  const { Picking } = flowComponents(selectedFlowKey());
  return <Picking orderIds={orderIds} onClose={onClose} roundId={ctx.roundId} picker={ctx.actor} cartId={cart?.id ?? null} viewOnly={!!ctx.viewOnly} />;
}

function PackingRoute() {
  const navigate = useNavigate();
  const isDevice = !!localStorage.getItem('wms_is_device');
  const [ready, setReady] = useState(!isDevice);
  const { PackingSession: Session } = flowComponents(selectedFlowKey());

  if (!ready) {
    // A tablet does not know who is packing; ask once per session. The name
    // is kept in the tracking context so every scan is attributed to it.
    return <WhoIsPicking orderIds={[]} role="packer"
             onReady={c => { setTrackContext({ actor: c.actor, roundId: null }); setReady(true); }}
             onCancel={() => navigate(-1)} />;
  }
  // The ONE packing route (Fernando, 2026-10-05): the scan session, which waits for a delivery note's
  // barcode. The tick-and-pack window (flows `Packing`) is no longer reachable — it recorded no scans.
  return <Session onClose={() => navigate(-1)} />;
}

/** The pages a P&P station login may open (Fernando 20.10). The P&P screen /pp; the orders list /orders/:tab (not a
 *  custom label); Bring & HRX; Products; Returns incl. Accept returns; Statistics; Settings; Inventory; WH Labels. */
const STATION_PAGES = /^\/(pp|orders\/[^/]+|bring-hrx|packeta|inbounds(\/.*)?|products(\/.*)?|returns(\/.*)?|tasks(\/.*)?|statistics(\/.*)?|settings(\/.*)?|tools\/wgr-inventory(\/.*)?|tools\/wh-labels(\/.*)?|warehouse\/location\/.*)$/;

// ── Main App ──────────────────────────────────────────────────────────────────

export default function App() {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('wms_token'));
  const [username, setUsername] = useState<string>(() => localStorage.getItem('wms_username') || '');
  const [appRole, setAppRole] = useState<string>(() => localStorage.getItem('wms_role') || 'staff');
  // on a phone the menu is a drawer, closed until ☰ is tapped (2026-09-28)
  const isPhone = useIsPhone();
  const [sidebarOpen, setSidebarOpen] = useState(() => !isPhoneNow());
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [webshops, setWebshops] = useState<Webshop[]>([]);
  const [reviewCount, setReviewCount] = useState(0);
  const [openTasks, setOpenTasks] = useState(0);
  const wmsLogo = useBrandingLogo();

  const location = useLocation();
  const navigate = useNavigate();
  // choosing a page on a phone closes the drawer
  useEffect(() => { if (isPhone) setSidebarOpen(false); }, [location.pathname, isPhone]);
  // A newer version on the server (buildCheck.ts): the next page change is a full load, so nobody keeps old code
  // for hours (Fernando, 8.10). The inventory's read-only screens reload at once; a section reloads itself when idle.
  const stale = useBuildStale();
  const lastPath = React.useRef(location.pathname);
  useEffect(() => { startBuildCheck(); }, []);
  useEffect(() => {
    const changed = lastPath.current !== location.pathname;
    lastPath.current = location.pathname;
    if (!stale) return;
    if (changed || /^\/tools\/wgr-inventory\/(dashboard|work-area)\/?$/.test(location.pathname)) reloadFresh();
  }, [location.pathname, stale]);

  const handleLogin = (t: string, u: string) => {
    setToken(t); setUsername(u);
    setAppRole(localStorage.getItem('wms_role') || 'staff');
  };
  // Every timestamp on screen is shown on the warehouse clock (time.ts); fetch
  // the setting once per signed-in session.
  useEffect(() => { if (token) loadWarehouseTz(); }, [token]);

  // A registered P&P station or cart tablet (Fernando 19.10: "I want the Orders page to only have 1 button … for
  // Station account all the other buttons should be not visible"). The SERVER says which (pp2/whoami), so a laptop
  // signed in before this existed switches too; '' = a person's login, undefined = not known yet (a device flag is
  // set, so nothing is shown until the server answers). The server enforces the same (device_guard.py).
  const [ppDevice, setPpDevice] = useState<string | undefined>(() => {
    const v = localStorage.getItem('wms_pp_device');
    if (v !== null) return v;
    return localStorage.getItem('wms_is_device') ? undefined : '';
  });
  // a Tier 1 person (floor rights without orders_page): the Orders page is for looking only (Fernando 20.10 —
  // "maximum guardrails"; the server refuses their order changes, device_guard.py)
  const [ordersLocked, setOrdersLocked] = useState(false);
  useEffect(() => {
    if (!token) return;
    // only a real answer changes the mode; without one (network, 5xx) the last known mode stays, and a login known
    // only as "a device" gets the station's narrow screen (the server enforces it anyway) — code review 20.10
    const unknown = () => setPpDevice(k => (k !== undefined ? k : (localStorage.getItem('wms_is_device') ? 'station' : '')));
    authFetch(`${API}/pp2/whoami`).then(async r => {
      if (!r.ok) { unknown(); return; }
      const d = await r.json();
      const kind = (d && d.device && d.device.kind) || '';
      localStorage.setItem('wms_pp_device', kind);
      setPpDevice(kind);
      setOrdersLocked(!!(d && d.orders_locked));
    }).catch(unknown);
  }, [token]);

  const handleLogout = useCallback(async () => {
    const sessionId = localStorage.getItem('wms_session_id');
    try {
      await authFetch(`${API}/logout`, {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId ? parseInt(sessionId) : null }),
      });
    } catch { /* don't block logout if server unreachable */ }
    localStorage.removeItem('wms_token');
    localStorage.removeItem('wms_username');
    localStorage.removeItem('wms_role');
    localStorage.removeItem('wms_first_name');
    localStorage.removeItem('wms_session_id');
    localStorage.removeItem('wms_pp_device');
    localStorage.removeItem('wms_is_device');
    setPpDevice('');                 // the next login in this tab starts as a person (a device login reloads the page)
    setOrdersLocked(false);
    setToken(null);
    setUsername('');
    setAppRole('staff');
  }, []);

  const fetchTabs = useCallback(async () => {
    const res = await authFetch(`${API}/tabs`);
    if (res.status === 401) { handleLogout(); return; }
    setTabs(await res.json());
  }, [handleLogout]);

  const fetchWebshops = useCallback(async () => {
    const res = await authFetch(`${API}/settings/webshops`);
    if (res.ok) setWebshops(await res.json());
  }, []);

  // Review count for the sidebar badge. Failure is swallowed on purpose: a review
  // endpoint that is down must not take the whole shell with it. It goes quiet, it
  // does not go wrong.
  const fetchReview = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/review/summary`);
      if (res.ok) setReviewCount((await res.json()).total ?? 0);
    } catch { /* leave the last known count */ }
  }, []);
  // open Tasks for the sidebar badge (1.11) — same contract: quiet on failure
  const fetchTasks = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/tasks/open-count`);
      if (res.ok) setOpenTasks((await res.json()).open ?? 0);
    } catch { /* leave the last known count */ }
  }, []);

  useEffect(() => {
    if (token) { fetchTabs(); fetchWebshops(); fetchReview(); fetchTasks(); }
  }, [token, fetchTabs, fetchWebshops, fetchReview, fetchTasks]);
  useEffect(() => {
    if (!token) return;
    const h = setInterval(fetchTasks, 120000);
    return () => clearInterval(h);
  }, [token, fetchTasks]);

  // Five minutes. These are exceptions, not a live queue — nobody is waiting on the
  // second, and this runs for every logged-in browser all day.
  useEffect(() => {
    if (!token) return;
    const t = setInterval(fetchReview, 300000);
    return () => clearInterval(t);
  }, [token, fetchReview]);

  // Per-user appearance + branding (logo) — server is the source of truth,
  // localStorage is just the instant-apply cache
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await authFetch(`${API}/prefs`);
        if (r.ok) applyAppearance(await r.json());
        const b = await authFetch(`${API}/branding`);
        if (b.ok) storeBranding(await b.json());
      } catch { /* keep cached appearance */ }
    })();
  }, [token]);

  // Orders → Bring & HRX lights up on the days Bring can go (guided P&P phase 7); not polled on the station/tablet screens
  const bringGo = useBringGo(!!token && !['/picking', '/packing', '/pp'].includes(location.pathname));
  // Orders → Packeta glows from N minutes before the Packeta pickup until the handover is started (Fernando 3.11)
  const packetaClock = usePacketaClock(!!token && !['/picking', '/packing', '/pp'].includes(location.pathname));
  const packetaGlow = !!packetaClock && (packetaClock.phase === 'soon' || packetaClock.phase === 'due');

  if (!token) return <LoginPage onLogin={handleLogin} />;

  // A P&P station sees the Orders page (one button: Start picking & packing round) and its station screen, nothing
  // else (Fernando 19.10). The cart tablets are not locked: they still run the old picking flow, the fallback while
  // the guided flow is new (device_guard.py docstring; code review 20.10).
  if (ppDevice === undefined) {
    return <div className="fixed inset-0 bg-[#0f1117] text-gray-400 flex items-center justify-center">Loading…</div>;
  }
  // an inbound station (29.10) is a station too: the same narrow screen, but it lives on the Inbounds page
  const inboundStation = ppDevice === 'inbound';
  // a moving station (2.11, Fernando: "add the Moving station option"): carried while moving pieces, lives on Tasks
  const movingStation = ppDevice === 'moving';
  const station = ppDevice === 'station' || inboundStation || movingStation;
  const stationHome = inboundStation ? '/inbounds' : movingStation ? '/tasks' : '/orders/new';
  // a station's pages (Fernando 20.10: "Orders, Bring & HRX, Inbounds, Returns, Products, Accept returns,
  // Statistics, Settings, Inventory, WH Labels") + its P&P screen; anything else → its home page
  if (station && !STATION_PAGES.test(location.pathname)) {
    return <Navigate to={stationHome} replace />;
  }
  // an inbound or moving station has no P&P: it may LOOK at the Orders page (31.10: every station sees everything,
  // works only on its own thing — the big button is the P&P station's) but never runs the guided round
  if ((inboundStation || movingStation) && location.pathname === '/pp') {
    return <Navigate to={stationHome} replace />;
  }
  // Tier 1: no old picking / packing screens on their own login
  if (ordersLocked && (location.pathname === '/picking' || location.pathname === '/packing')) {
    return <Navigate to="/orders/new" replace />;
  }

  const newCount = tabs.find(t => t.key === 'new')?.count ?? 0;
  const onOrders = location.pathname === '/' || location.pathname.startsWith('/orders');
  const onBringHrx   = location.pathname.startsWith('/bring-hrx');
  const onPacketa    = location.pathname.startsWith('/packeta');
  const onInbounds   = location.pathname.startsWith('/inbounds');
  const onSettings   = location.pathname.startsWith('/settings');
  const onTodo       = location.pathname.startsWith('/todo');
  const onReview     = location.pathname.startsWith('/review');
  const onStatistics = location.pathname.startsWith('/statistics');
  const onWorkTime   = location.pathname.startsWith('/statistics/work-time');
  const onLabelDoc   = location.pathname.startsWith('/statistics/label-doctor');
  const onParcels    = location.pathname.startsWith('/statistics/parcels');
  const onTools      = location.pathname.startsWith('/tools');
  const onPrintLabel = location.pathname.startsWith('/tools/print-label');
  const onWhLabels   = location.pathname.startsWith('/tools/wh-labels');
  const onInformation = location.pathname.startsWith('/information/motley-denim');
  const onInstructions = location.pathname.startsWith('/information/instructions');
  const onReturns    = location.pathname.startsWith('/returns');
  const onAccept     = location.pathname.startsWith('/returns/accept');
  const onTasks      = location.pathname.startsWith('/tasks');          // Tasks (1.11): its own top-level item
  const onProducts   = location.pathname.startsWith('/products');
  const onWarehouse  = location.pathname.startsWith('/warehouse/');   // the tool pages of Settings → Warehouse

  // Picking and packing are full-screen routes — hide shell
  // /pp: the guided P&P screen (station / cart tablet, 2026-10-17) — full screen like the old walk and packing
  const isFullscreen = location.pathname === '/picking' || location.pathname === '/packing' || location.pathname === '/pp';

  if (isFullscreen) {
    return (
      <Routes>
        <Route path="/picking" element={<PickingRoute />} />
        <Route path="/packing" element={<PackingRoute />} />
        <Route path="/pp" element={<PpApp />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-[#1a1d27] border-b border-gray-800 px-3 sm:px-4 py-3 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <button onClick={() => setSidebarOpen(o => !o)}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors">
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="2" y1="5" x2="16" y2="5"/>
              <line x1="2" y1="9" x2="16" y2="9"/>
              <line x1="2" y1="13" x2="16" y2="13"/>
            </svg>
          </button>
          {wmsLogo ? (
            <img src={wmsLogo} alt="WMS logo" className="h-7 max-w-[160px] object-contain" />
          ) : (
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center font-bold text-xs text-white">W</div>
          )}
          <span className="font-semibold text-white text-sm hidden sm:inline">Motley Denim WMS</span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="w-2 h-2 bg-green-400 rounded-full"></span>
          <span className="text-gray-400 hidden sm:inline">Live</span>
          <span className="text-gray-600 hidden sm:inline">|</span>
          <span className="text-gray-400 hidden sm:inline">
            {localStorage.getItem('wms_first_name') || username}
          </span>
          <button onClick={handleLogout} className="text-gray-500 hover:text-white transition-colors text-xs">
            Sign out
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {isPhone && sidebarOpen && <div className="fixed inset-0 top-14 z-30 bg-black/50" onClick={() => setSidebarOpen(false)} />}
        <aside className={isPhone
          ? `fixed left-0 top-14 bottom-0 z-40 w-56 bg-[#1a1d27] border-r border-gray-800 py-4 overflow-y-auto transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
          : `bg-[#1a1d27] border-r border-gray-800 py-4 flex-none transition-all duration-200 overflow-hidden ${sidebarOpen ? 'w-48' : 'w-0'}`}>
          <nav className="space-y-0.5 px-2 w-48">
            <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Warehouse</div>
            <button onClick={() => navigate('/orders/new')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onOrders ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              <span>Orders</span>
              {newCount > 0 && (
                <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">{newCount}</span>
              )}
            </button>
            {/* Bring & HRX (guided P&P phase 7, 2026-10-17): Fernando — "highlighted when Bring can be sent today" */}
            <button onClick={() => navigate('/bring-hrx')}
              className={`w-full flex items-center justify-between pl-6 pr-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onBringHrx ? 'bg-blue-600 text-white' : bringGo ? 'bg-emerald-900/40 text-emerald-200 hover:bg-emerald-800/50' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              <span>Bring & HRX</span>
              {bringGo && <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full">today</span>}
            </button>
            {/* Packeta handovers (3.11): one per pickup day; glows before the pickup — "9:50 AM the Packeta handover button
                should highlight … the WMS says it is soon time to prepare Packeta parcels for pickup" */}
            <button onClick={() => navigate('/packeta')}
              title={packetaGlow && packetaClock?.pickup_at ? `Soon time to prepare the Packeta parcels for pickup (${fmtWh(packetaClock.pickup_at, 'time')})` : undefined}
              className={`w-full flex items-center justify-between pl-6 pr-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onPacketa ? 'bg-blue-600 text-white' : packetaGlow ? 'bg-amber-500 text-black animate-pulse' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              <span>Packeta</span>
              {!!packetaClock?.open && <span className="bg-gray-700 text-white text-xs px-2 py-0.5 rounded-full">{packetaClock.open.count}</span>}
            </button>
            {/* Inbounds (2026-10-29, docs/INBOUND.md): goods receiving into numbered boxes at the inbound stations */}
            <button onClick={() => navigate('/inbounds')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onInbounds ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              Inbounds
            </button>
            <button onClick={() => navigate('/products')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onProducts ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              Products
            </button>
            {/* Calibrate / Floor plan / Mark stacks / Units moved under Settings → Warehouse (2026-09-29) */}
            <button onClick={() => navigate('/returns/list')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onReturns && !onAccept ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              Returns
            </button>
            <button onClick={() => navigate('/returns/accept')}
              className={`w-full flex items-center pl-6 pr-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onAccept ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              Accept returns
            </button>
            {/* Tasks (Fernando 1.11): one-time warehouse tasks — a top-level item between Returns and Statistics */}
            <button onClick={() => navigate('/tasks')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onTasks ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              Tasks
              {openTasks > 0 && <span className="ml-auto text-[11px] px-1.5 py-0.5 rounded-full bg-amber-600 text-white">{openTasks}</span>}
            </button>
            <button onClick={() => navigate('/statistics')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onStatistics && !onWorkTime && !onLabelDoc && !onParcels ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              Statistics
            </button>
            {/* Parcels moved here from Warehouse (parcel tracking v2, 2026-10-02) — every role, as before */}
            <button onClick={() => navigate('/statistics/parcels/states')}
              className={`w-full flex items-center pl-6 pr-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onParcels ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              Parcels
            </button>
            {appRole === 'admin' && (
              <button onClick={() => navigate('/statistics/work-time')}
                className={`w-full flex items-center pl-6 pr-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  onWorkTime ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}>
                Work time
              </button>
            )}
            {appRole === 'admin' && (
              <button onClick={() => navigate('/statistics/label-doctor')}
                className={`w-full flex items-center pl-6 pr-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  onLabelDoc ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}>
                Label doctor
              </button>
            )}
            <button onClick={() => navigate(
              appRole === 'admin' ? '/settings/stores'
              : appRole === 'manager' || appRole === 'staff' ? '/settings/warehouse'
              : '/settings/appearance'
            )}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onSettings || onWarehouse ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              Settings
            </button>

            {appRole === 'admin' && (
              <button onClick={() => navigate('/todo')}
                className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  onTodo ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}>
                To-do list
              </button>
            )}

            {/* Review sits last on purpose: it is not a queue you work through, it is
                the place the system puts what it would not decide alone. The badge is
                red rather than blue because unlike Orders, a number here means
                something is already wrong. — Claude */}
            {(appRole === 'admin' || appRole === 'manager') && (
              <button onClick={() => navigate('/review')}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  onReview ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}>
                <span>Review</span>
                {reviewCount > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{reviewCount}</span>
                )}
              </button>
            )}

            <div className="px-3 py-1.5 mt-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tools</div>
            <button onClick={() => navigate('/tools/wgr-inventory/work-area')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onTools && !onPrintLabel && !onWhLabels ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              Inventory
            </button>
            {/* Opened to warehouse staff 2026-07-26 (was admin-only) — the packers are
                the ones who need an ad-hoc label. Backend gates on MANUAL_LABEL_ROLES. */}
            {!station && <button onClick={() => navigate('/tools/print-label')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onPrintLabel ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              Print a shipping label
            </button>}
            <button onClick={() => navigate('/tools/wh-labels')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onWhLabels ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              WH Labels
            </button>

            {!station && (<>
            <div className="px-3 py-1.5 mt-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Information</div>
            <button onClick={() => navigate('/information/motley-denim/shipping')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onInformation ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              Motley Denim
            </button>
            <button onClick={() => navigate('/information/motley-denim/shipping')}
              className={`w-full flex items-center pl-6 pr-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onInformation ? 'text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              Shipping
            </button>
            <button onClick={() => navigate('/information/instructions')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                onInstructions ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}>
              Instructions
            </button>
            </>)}
          </nav>
        </aside>

        <main className="flex-1 overflow-auto">
          <AnnouncementBar className="px-4 pt-2" />
          {stale && (
            <div className="sticky top-0 z-40 flex flex-wrap items-center gap-3 px-4 py-2 bg-blue-950/95 border-b border-blue-800 text-blue-100 text-sm">
              <span>A new version of the WMS is ready — it loads when you open another page.</span>
              <button onClick={() => reloadFresh(true)} className="px-3 py-1 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold">Reload now</button>
            </div>
          )}
          <Routes>
            <Route path="/" element={<Navigate to="/orders/new" replace />} />
            <Route path="/orders" element={<Navigate to="/orders/new" replace />} />
            <Route path="/orders/:tab" element={
              <OrdersPage tabs={tabs} webshops={webshops} onRefreshTabs={fetchTabs} station={ppDevice === 'station'} locked={ordersLocked || inboundStation || movingStation} />
            } />
            <Route path="/orders/:orderId/custom-label" element={<CustomOrderLabelPage />} />
            <Route path="/bring-hrx" element={<BringHrxPage />} />
            <Route path="/packeta" element={<PacketaPage />} />
            <Route path="/inbounds" element={<InboundsPage />} />
            <Route path="/inbounds/open" element={<InboundsPage tab="open" />} />
            <Route path="/inbounds/done" element={<InboundsPage tab="done" />} />
            <Route path="/inbounds/archived" element={<InboundsPage tab="archived" />} />
            <Route path="/inbounds/:id" element={<InboundDetail />} />
            <Route path="/inbounds/:id/replenish" element={<InboundReplenish />} />
            <Route path="/inbounds/:id/move/:mid" element={<InboundMove />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/move/:mid" element={<ProductMove />} />
            <Route path="/products/:wmsId" element={<ProductPage />} />
            <Route path="/warehouse/calibrate" element={<CalibrateAisles />} />
            <Route path="/warehouse/calibrate/layout" element={<LayoutFromSections />} />
            <Route path="/warehouse/calibrate/face/:letter" element={<UnitsWalk />} />
            <Route path="/warehouse/calibrate/:aisleId" element={<CalibrateWalk />} />
            <Route path="/warehouse/units" element={<UnitsPage />} />
            <Route path="/warehouse/floor" element={<Navigate to="/settings/warehouse/floor" replace />} />
            <Route path="/warehouse/stacks" element={<MarkStacks />} />
            <Route path="/warehouse/location/:code" element={<LocationPage />} />
            <Route path="/warehouse/pieces" element={<PiecesPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/tasks/:id" element={<TaskScreen />} />
            <Route path="/parcels" element={<Navigate to="/statistics/parcels/states" replace />} />
            <Route path="/settings" element={<Navigate to="/settings/stores" replace />} />
            <Route path="/settings/:section" element={<SettingsPage />} />
            <Route path="/settings/warehouse/aisles/:aisleId" element={<SettingsPage section="warehouse" sub="aisles" />} />
            <Route path="/settings/:section/:sub" element={<SettingsPage />} />
            <Route path="/statistics/work-time" element={<WorkTimePage />} />
            <Route path="/statistics/label-doctor" element={<LabelDoctorStats />} />
            <Route path="/statistics/parcels" element={<Navigate to="/statistics/parcels/states" replace />} />
            <Route path="/statistics/parcels/:view" element={<ParcelsSection />} />
            <Route path="/statistics" element={<StatisticsPage />} />
            <Route path="/returns" element={<Navigate to="/returns/list" replace />} />
            <Route path="/returns/accept" element={<ReturnsDesk />} />
            <Route path="/returns/order/:id" element={<ReturnOrderDetail />} />
            <Route path="/returns/order/:id/replenish" element={<ReturnReplenish />} />
            <Route path="/returns/:tab" element={<ReturnsPage />} />
            <Route path="/tools/print-label" element={<ManualLabelPage />} />
            <Route path="/tools/wh-labels" element={<WHLabelsPage />} />
            <Route path="/todo" element={<AdminTasksPage />} />
            <Route path="/review" element={<ReviewPage />} />
            <Route path="/tools/wgr-inventory" element={<Navigate to="/tools/wgr-inventory/work-area" replace />} />
            <Route path="/tools/wgr-inventory/dashboard" element={<WgrDashboard />} />
            <Route path="/tools/wgr-inventory/work-area" element={<WgrWorkArea />} />
            <Route path="/tools/wgr-inventory/work-area/:stockplace" element={<WgrSection />} />
            <Route path="/tools/wgr-inventory/statistics" element={<WgrStatistics />} />
            <Route path="/tools/wgr-inventory/manager" element={<WgrManager />} />
            <Route path="/information" element={<Navigate to="/information/motley-denim/shipping" replace />} />
            <Route path="/information/motley-denim" element={<Navigate to="/information/motley-denim/shipping" replace />} />
            <Route path="/information/motley-denim/shipping" element={<WgrShippingInfo />} />
            <Route path="/information/instructions" element={<InstructionsPage />} />
            <Route path="/information/instructions/:slug" element={<InstructionsPage />} />
            <Route path="*" element={<Navigate to="/orders/new" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
